import { Link } from "@tanstack/react-router";
import { EyebrowLabel } from "@/components/ui-ext/Badge";

/**
 * Structured internal-link cluster for the priority location pages.
 *
 * Search Console shows Austin, Dallas, and Georgetown as the three location
 * URLs closest to breaking through, with the local-SEO guides hub as the
 * strongest supporting asset. Rather than relying on the generic "nearby
 * markets" grid — which links by geography, not by opportunity — this block
 * hard-wires descriptive, keyword-bearing links between those pages and the
 * service pages they should be passing authority to.
 *
 * Anchor text is intentionally descriptive rather than "click here"/"Dallas":
 * the anchor is the strongest on-page relevance signal we control.
 */

type ClusterLink =
  | { kind: "location"; slug: string; anchor: string; note: string }
  | { kind: "service"; slug: string; anchor: string; note: string }
  | { kind: "guides"; slug: string; anchor: string; note: string }
  | { kind: "guide"; slug: string; anchor: string; note: string };

type Cluster = {
  heading: string;
  intro: string;
  links: ClusterLink[];
};

const CLUSTERS: Record<string, Cluster> = {
  austin: {
    heading: "Where Austin connects across our network.",
    intro:
      "Austin is our home market and the hub of our internal link graph. These are the pages an Austin programme most often extends into — the North Texas metro we run corridor work in, the Williamson County market on the same operational route, and the service pages that carry the Austin work.",
    links: [
      {
        kind: "location",
        slug: "dallas",
        anchor: "Dallas SEO services for the DFW Metroplex",
        note: "Our largest North Texas market — corridor programmes run from Austin.",
      },
      {
        kind: "location",
        slug: "georgetown",
        anchor: "Georgetown SEO for Williamson County",
        note: "The northern Central Texas corridor, on the same operational route as Austin.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO and Local Pack programmes",
        note: "The service that carries most Austin engagements.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "Every playbook and field note behind the local work.",
      },
      {
        kind: "service",
        slug: "aeo-geo",
        anchor: "AEO and GEO for AI search visibility",
        note: "Austin's tech-heavy buyer base adopts AI search ahead of the curve.",
      },
      {
        kind: "guide",
        slug: "austin-link-building-strategy",
        anchor: "our Austin link building strategy guide",
        note: "The pillar guide on earning genuinely local Austin links.",
      },
    ],
  },
  dallas: {
    heading: "Where Dallas connects across our network.",
    intro:
      "Dallas work rarely stops at the county line. These are the markets and services a Metroplex programme routinely reaches into, and the reading that explains the methodology behind the Dallas engagement.",
    links: [
      {
        kind: "location",
        slug: "austin",
        anchor: "Austin SEO from our Central Texas HQ",
        note: "Where the team, the strategy, and the case studies originate.",
      },
      {
        kind: "location",
        slug: "georgetown",
        anchor: "Georgetown SEO in Williamson County",
        note: "Our Central Texas growth-corridor market.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO for multi-location operators",
        note: "The core service behind every DFW Local Pack programme.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "Dallas-specific worked examples live here.",
      },
      {
        kind: "service",
        slug: "technical-seo",
        anchor: "technical SEO for competitive metro SERPs",
        note: "What it takes to compete against fifteen-year-old Dallas domains.",
      },
      {
        kind: "service",
        slug: "content-marketing",
        anchor: "content marketing built to earn AI citations",
        note: "Dallas B2B buyers increasingly shortlist through AI assistants.",
      },
    ],
  },
  georgetown: {
    heading: "Where Georgetown connects across our network.",
    intro:
      "Georgetown programmes run as part of a Central Texas corridor, not in isolation. These are the pages that carry the strategy, the methodology, and the neighbouring markets we operate in on the same route.",
    links: [
      {
        kind: "location",
        slug: "austin",
        anchor: "Austin SEO from our South Austin HQ",
        note: "Thirty minutes south — same team, same operational week.",
      },
      {
        kind: "location",
        slug: "round-rock",
        anchor: "Round Rock SEO in southern Williamson County",
        note: "The other half of the Williamson County corridor.",
      },
      {
        kind: "location",
        slug: "dallas",
        anchor: "Dallas SEO services across the Metroplex",
        note: "How the same discipline scales into a top-five US metro.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO for growth-market service businesses",
        note: "The primary service behind Georgetown engagements.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "Georgetown-specific worked examples live here.",
      },
      {
        kind: "service",
        slug: "google-business-profile",
        anchor: "Google Business Profile management",
        note: "The single highest-leverage asset in a fast-growing market.",
      },
    ],
  },
  houston: {
    heading: "Where Houston connects across our network.",
    intro:
      "Houston is the largest impression driver in our Search Console data, and Houston programmes almost always extend north into the Montgomery County corridor or west across the state. These are the markets and services a Gulf Coast engagement reaches into.",
    links: [
      {
        kind: "location",
        slug: "the-woodlands",
        anchor: "The Woodlands SEO for the Montgomery County corridor",
        note: "The high-income corridor north of Houston, run as its own programme.",
      },
      {
        kind: "location",
        slug: "san-antonio",
        anchor: "San Antonio SEO across Bexar County",
        note: "Our other major South Texas market on the same operational model.",
      },
      {
        kind: "location",
        slug: "dallas",
        anchor: "Dallas SEO services for the DFW Metroplex",
        note: "How multi-catchment work scales in Texas's other giant metro.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO and Local Pack programmes",
        note: "The service that carries catchment-based Houston engagements.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "The catchment and GBP playbooks behind the Houston work.",
      },
      {
        kind: "service",
        slug: "aeo-geo",
        anchor: "AEO and GEO for AI search visibility",
        note: "Houston B2B adopts AI-assisted vendor research ahead of the curve.",
      },
    ],
  },
  "san-antonio": {
    heading: "Where San Antonio connects across our network.",
    intro:
      "San Antonio work runs as a Bexar–Comal regional programme with links out to the markets that share its operational model. These are the pages a South Texas engagement most often extends into.",
    links: [
      {
        kind: "location",
        slug: "austin",
        anchor: "Austin SEO from our South Austin HQ",
        note: "Eighty miles north — the same team runs both markets.",
      },
      {
        kind: "location",
        slug: "houston",
        anchor: "Houston SEO across the Gulf Coast",
        note: "Our largest Texas metro programme, on the same catchment model.",
      },
      {
        kind: "service",
        slug: "google-business-profile",
        anchor: "Google Business Profile management",
        note: "The fastest-moving lever in a market where GBP hygiene is weak.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO for South Texas service businesses",
        note: "The primary service behind San Antonio engagements.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "Worked examples for corridor and catchment strategy.",
      },
      {
        kind: "service",
        slug: "aeo-geo",
        anchor: "AEO and GEO for AI search visibility",
        note: "Adoption trails Austin by a year — which is exactly why to start now.",
      },
    ],
  },
  "the-woodlands": {
    heading: "Where The Woodlands connects across our network.",
    intro:
      "Corridor programmes here run alongside our Houston work but to a different content standard. These are the pages a Montgomery County engagement is built on.",
    links: [
      {
        kind: "location",
        slug: "houston",
        anchor: "Houston SEO across the Gulf Coast metro",
        note: "The parent market — different buyer, shared operational region.",
      },
      {
        kind: "location",
        slug: "austin",
        anchor: "Austin SEO from our Central Texas HQ",
        note: "Where the programme methodology is built and tested.",
      },
      {
        kind: "service",
        slug: "content-strategy",
        anchor: "content strategy for high-consideration buyers",
        note: "Editorial depth is what wins high-income corridor categories.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO and Local Pack programmes",
        note: "Per-catchment work across The Woodlands, Spring, Conroe, Magnolia.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "The catchment mapping methodology in full.",
      },
      {
        kind: "service",
        slug: "aeo-geo",
        anchor: "AEO and GEO for AI search visibility",
        note: "Affluent, research-heavy buyers shortlist providers inside AI assistants.",
      },
    ],
  },
  "fort-worth": {
    heading: "Where Fort Worth connects across our network.",
    intro:
      "Fort Worth work is run as a Tarrant County programme rather than a Dallas add-on, but the two share a team, a link graph, and a measurement model. These are the pages a Fort Worth engagement most often extends into.",
    links: [
      {
        kind: "location",
        slug: "dallas",
        anchor: "Dallas SEO services for the DFW Metroplex",
        note: "The other half of the Metroplex — separate strategy, shared operations.",
      },
      {
        kind: "location",
        slug: "plano",
        anchor: "Plano SEO for Collin County",
        note: "Where our North Texas corporate-HQ and local work overlaps.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO and Local Pack programmes",
        note: "The service that carries most Tarrant County engagements.",
      },
      {
        kind: "service",
        slug: "google-business-profile",
        anchor: "Google Business Profile management",
        note: "The fastest lever in a market where GBP hygiene is still uneven.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "The catchment and review playbooks behind the Fort Worth work.",
      },
      {
        kind: "service",
        slug: "aeo-geo",
        anchor: "AEO and GEO for AI search visibility",
        note: "Tarrant County AI-search competition is thinner than Dallas — an early-mover window.",
      },
    ],
  },
  "round-rock": {
    heading: "Where Round Rock connects across our network.",
    intro:
      "Round Rock runs as a Williamson County corridor programme alongside Georgetown and Cedar Park, with Austin as the methodology hub. These are the pages a corridor engagement is built on.",
    links: [
      {
        kind: "location",
        slug: "georgetown",
        anchor: "Georgetown SEO for northern Williamson County",
        note: "The next market north — same corridor, different buyer profile.",
      },
      {
        kind: "location",
        slug: "cedar-park",
        anchor: "Cedar Park and Leander SEO on the 183A corridor",
        note: "The corridor's western half, run on the same model.",
      },
      {
        kind: "location",
        slug: "austin",
        anchor: "Austin SEO from our South Austin HQ",
        note: "Twenty miles south — where the corridor methodology is built.",
      },
      {
        kind: "service",
        slug: "local-seo",
        anchor: "local SEO and Local Pack programmes",
        note: "The primary service behind Round Rock service-area work.",
      },
      {
        kind: "guides",
        slug: "local-seo",
        anchor: "the local SEO guides hub",
        note: "Service-area page systems and GBP playbooks in full.",
      },
      {
        kind: "service",
        slug: "google-business-profile",
        anchor: "Google Business Profile management",
        note: "Corridor Local Pack moves faster than Central Austin when GBP is run properly.",
      },
    ],
  },
};


export function hasPriorityCluster(slug: string) {
  return Boolean(CLUSTERS[slug]);
}

function ClusterAnchor({ link }: { link: ClusterLink }) {
  const className = "font-medium text-primary hover:underline underline-offset-4";
  if (link.kind === "location") {
    return (
      <Link to={"/seo-agency-{$slug}" as any} params={{ slug: link.slug } as any} className={className}>
        {link.anchor}
      </Link>
    );
  }
  if (link.kind === "service") {
    return (
      <Link to="/services/$slug/" params={{ slug: link.slug }} className={className}>
        {link.anchor}
      </Link>
    );
  }
  if (link.kind === "guides") {
    return (
      <Link
        to="/services/$slug/guides/"
        params={{ slug: link.slug }}
        search={{ tag: "", page: 1 }}
        className={className}
      >
        {link.anchor}
      </Link>
    );
  }
  return (
    <Link to="/guides/$slug/" params={{ slug: link.slug }} className={className}>
      {link.anchor}
    </Link>
  );
}

export function PriorityLinkCluster({ slug }: { slug: string }) {
  const cluster = CLUSTERS[slug];
  if (!cluster) return null;

  return (
    <section
      id="connected"
      className="scroll-mt-24 mt-16 border-t border-border pt-12"
    >
      <EyebrowLabel>Connected pages</EyebrowLabel>
      <h2 className="mt-4 font-display text-3xl md:text-4xl">{cluster.heading}</h2>
      <p className="mt-3 max-w-2xl text-sm md:text-base text-muted-foreground leading-relaxed">
        {cluster.intro}
      </p>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2">
        {cluster.links.map((l) => (
          <li key={`${l.kind}-${l.slug}-${l.anchor}`} className="surface-card p-5">
            <ClusterAnchor link={l} />
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              {l.note}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
